import React, { FC, useState } from 'react';
import { toast } from 'react-hot-toast';
import axiosInstant from '../../api';
import { getVideoFileV2 } from '../../api/endpoints';
import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { isAM } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { Button } from '../Button/Button';
import { getVideoFilename } from './videoStatus';
import { softDeleteErrorCode, softDeleteErrorMessage } from './videoSoftDelete';

const restoreIc = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12a8 8 0 1 0 2.3-5.7" />
    <path d="M4 4v5h5" />
  </svg>
);

// Codes the API sends without a message of their own.
const CODE_MESSAGES: Record<string, string> = {
  FileNotDeleted: 'This video is not deleted. It may already have been restored.',
  FileNotFound: 'This video no longer exists.',
};

interface Props {
  video: IVideo;
  title: string;
  /** Refetch the list: the restored video leaves the Deleted view. */
  onRestored: () => void;
}

/** The one action on a deleted video's card: PATCH /files/:id { deactivated: false }. */
export const RestoreVideoButton: FC<Props> = ({ video, title, onRestored }) => {
  const { role } = useUser();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!isAM(role)) return null;

  const filename = getVideoFilename(video);
  const projectId = video.projectId || (video as any).project?.id;

  const handleRestore = async () => {
    setBusy(true);
    try {
      // ONLY `deactivated`: the same route edits the file's other fields.
      await axiosInstant.patch(getVideoFileV2(video.id), { deactivated: false });
      toast.success('Video restored');
      setOpen(false);
      onRestored();
    } catch (err: any) {
      const status = err?.response?.status;
      // These two arrive as bare codes (no message); 409 ProjectDeactivated
      // (restore the project instead) and 409 FileReplaced (a new video took
      // its place) carry their own message.
      const code = softDeleteErrorCode(err);
      // A 403 already got the global "no permission" toast (api/index.ts).
      if (status !== 403) {
        toast.error(softDeleteErrorMessage(err, 'Failed to restore video. Please try again.', CODE_MESSAGES));
      }
      // A definite answer (already live, gone, its project is deleted, or
      // replaced): nothing to retry, and the first two mean the row is stale.
      if (status === 409 || status === 404) setOpen(false);
      if (code === 'FileNotDeleted' || status === 404) onRestored();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        title="Restore video"
        icon={restoreIc}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="!flex items-center justify-center !min-w-0 !h-[30px] !px-2.5 !text-[12px] gap-1.5 whitespace-nowrap"
      >
        Restore video
      </Button>
      <ConfirmationDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleRestore}
        disabled={busy}
        title="Restore video"
        description={[
          `Restore “${title}” (${filename || 'no file'})?`,
          `It goes back to ${projectId ? 'its project and ' : ''}the video lists for everyone who could see it before.${
            projectId ? ' If a new video was uploaded in its place, it can’t be restored.' : ''
          }`,
        ]}
        confirmLabel="Restore"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default RestoreVideoButton;
